import React, { useState } from 'react';
import { HomeIcon, ChevronRightIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, BookOpenIcon, MessageSquareIcon, PlayCircleIcon, PhoneIcon, PlusIcon, FilterIcon, FileTextIcon, AlertCircleIcon, XIcon, SendIcon, CheckCircleIcon, AlertTriangleIcon, AlertOctagonIcon, ClipboardIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// Types
interface FAQ {
  category: string;
  questions: {
    id: string;
    question: string;
    answer: string;
  }[];
}
interface SupportRequest {
  reference: string;
  subject: string;
  status: 'Open' | 'In Progress' | 'Resolved';
  dateSubmitted: string;
  lastUpdated: string;
}
interface VideoTutorial {
  id: string;
  title: string;
  duration: string;
  thumbnail: string;
  description: string;
}
const Support = () => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState<string[]>([]);
  const [expandedFAQs, setExpandedFAQs] = useState<string[]>([]);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('faq');
  // Mock data
  const faqs: FAQ[] = [{
    category: 'Shipment Creation',
    questions: [{
      id: 'faq-1',
      question: 'How do I create a new shipment?',
      answer: 'To create a new shipment, click the "Create Load" button in the top navigation bar. Fill out the required information in the form, including origin, destination, and cargo details. Click "Submit" to create your shipment.'
    }, {
      id: 'faq-2',
      question: 'What information do I need to create a shipment?',
      answer: 'You will need pickup and delivery addresses, contact information, cargo details (weight, dimensions, commodity type), and any special requirements or instructions.'
    }]
  }, {
    category: 'Shipment Management',
    questions: [{
      id: 'faq-3',
      question: 'How do I track my shipment?',
      answer: 'You can track your shipment by clicking on the shipment ID from your dashboard or shipments list. This will show you real-time location updates and estimated arrival times.'
    }]
  }];
  const videos: VideoTutorial[] = [{
    id: 'video-1',
    title: 'Getting Started with The Point TMS',
    duration: '3:45',
    thumbnail: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    description: 'Learn the basics of using The Point TMS platform.'
  }, {
    id: 'video-2',
    title: 'Creating Your First Shipment',
    duration: '5:20',
    thumbnail: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    description: 'Step-by-step guide to creating and managing shipments.'
  }];
  const supportRequests: SupportRequest[] = [{
    reference: 'SR-12345',
    subject: 'Cannot update delivery address',
    status: 'Open',
    dateSubmitted: '2024-01-15',
    lastUpdated: '2024-01-15'
  }, {
    reference: 'SR-12344',
    subject: 'Billing question for December invoices',
    status: 'In Progress',
    dateSubmitted: '2024-01-14',
    lastUpdated: '2024-01-15'
  }];
  // Handlers
  const toggleFAQ = (id: string) => {
    setExpandedFAQs(expandedFAQs.includes(id) ? expandedFAQs.filter(faqId => faqId !== id) : [...expandedFAQs, id]);
  };
  const handleSupportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setShowSubmissionSuccess(true);
    setTimeout(() => {
      setShowSupportForm(false);
      setShowSubmissionSuccess(false);
    }, 3000);
  };
  return <div className="py-4" data-id="element-1516">
      {/* Breadcrumbs */}
      <nav className="flex items-center text-sm text-gray-500 mb-4" data-id="element-1517">
        <Link to="/" className="hover:text-gray-700 flex items-center" data-id="element-1518">
          <HomeIcon size={16} className="mr-2" data-id="element-1519" />
          Home
        </Link>
        <ChevronRightIcon size={16} className="mx-2" data-id="element-1520" />
        <span className="text-gray-900" data-id="element-1521">Support</span>
      </nav>
      {/* Enhanced header */}
      <div className="mb-8" data-id="element-1522">
        <h1 className="text-3xl font-semibold text-gray-800 mb-2" data-id="element-1523">
          Support Center
        </h1>
        <p className="text-lg text-gray-600" data-id="element-1524">
          Find answers, documentation, and help for The Point TMS
        </p>
      </div>
      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1525">
        {/* Left column - Main content */}
        <div className="lg:col-span-2" data-id="element-1526">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-1527">
            {/* Enhanced tabs */}
            <div className="border-b border-gray-200" data-id="element-1528">
              <nav className="flex" data-id="element-1529">
                <button onClick={() => setActiveTab('faq')} className={`flex items-center py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === 'faq' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1530">
                  <BookOpenIcon size={18} className="mr-2" data-id="element-1531" />
                  Frequently Asked Questions
                </button>
                <button onClick={() => setActiveTab('videos')} className={`flex items-center py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === 'videos' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1532">
                  <PlayCircleIcon size={18} className="mr-2" data-id="element-1533" />
                  Video Tutorials
                </button>
                <button onClick={() => setActiveTab('requests')} className={`flex items-center py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === 'requests' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1534">
                  <MessageSquareIcon size={18} className="mr-2" data-id="element-1535" />
                  My Requests
                </button>
              </nav>
            </div>
            {/* Enhanced content sections */}
            <div className="p-6" data-id="element-1536">
              {activeTab === 'faq' && <div className="space-y-8" data-id="element-1537">
                  {faqs.map(category => <div key={category.category} data-id="element-1538">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1539">
                        <FileTextIcon size={20} className="mr-2 text-primary-600" data-id="element-1540" />
                        {category.category}
                      </h3>
                      <div className="space-y-3" data-id="element-1541">
                        {category.questions.map(faq => <div key={faq.id} className="border border-gray-200 rounded-lg transition-shadow hover:shadow-sm" data-id="element-1542">
                            <button onClick={() => toggleFAQ(faq.id)} className="w-full px-5 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors rounded-lg" data-id="element-1543">
                              <span className="text-sm font-medium text-gray-900 text-left" data-id="element-1544">
                                {faq.question}
                              </span>
                              {expandedFAQs.includes(faq.id) ? <ChevronUpIcon size={20} className="text-gray-400 flex-shrink-0 ml-4" data-id="element-1545" /> : <ChevronDownIcon size={20} className="text-gray-400 flex-shrink-0 ml-4" data-id="element-1546" />}
                            </button>
                            {expandedFAQs.includes(faq.id) && <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg" data-id="element-1547">
                                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1548">
                                  {faq.answer}
                                </p>
                              </div>}
                          </div>)}
                      </div>
                    </div>)}
                </div>}
              {activeTab === 'videos' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1549">
                  {videos.map(video => <div key={video.id} className="group border border-gray-200 rounded-lg overflow-hidden transition-shadow hover:shadow-md" data-id="element-1550">
                      <div className="relative aspect-video" data-id="element-1551">
                        <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" data-id="element-1552" />
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-40 transition-opacity" data-id="element-1553">
                          <PlayCircleIcon size={48} className="text-white opacity-90 group-hover:opacity-100 transform group-hover:scale-110 transition-transform" data-id="element-1554" />
                        </div>
                        <span className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded" data-id="element-1555">
                          {video.duration}
                        </span>
                      </div>
                      <div className="p-4" data-id="element-1556">
                        <h4 className="font-medium text-gray-900 mb-1" data-id="element-1557">
                          {video.title}
                        </h4>
                        <p className="text-sm text-gray-500" data-id="element-1558">
                          {video.description}
                        </p>
                      </div>
                    </div>)}
                </div>}
              {activeTab === 'requests' && <div data-id="element-1559">
                  <div className="mb-4 flex justify-between items-center" data-id="element-1560">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-1561">
                      Support Requests
                    </h3>
                    <button onClick={() => setShowSupportForm(true)} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700" data-id="element-1562">
                      <PlusIcon size={16} className="mr-2" data-id="element-1563" />
                      New Request
                    </button>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1564">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1565">
                      <thead data-id="element-1566">
                        <tr data-id="element-1567">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1568">
                            Reference
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1569">
                            Subject
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1570">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1571">
                            Date Submitted
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1572">
                            Last Updated
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-1573">
                        {supportRequests.map(request => <tr key={request.reference} data-id="element-1574">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600" data-id="element-1575">
                              {request.reference}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1576">
                              {request.subject}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-1577">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.status === 'Open' ? 'bg-yellow-100 text-yellow-800' : request.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-1578">
                                {request.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1579">
                              {request.dateSubmitted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1580">
                              {request.lastUpdated}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}
            </div>
          </div>
        </div>
        {/* Right column - Quick help */}
        <div className="space-y-6" data-id="element-1581">
          {/* Quick Links */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1582">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1583">
              Quick Links
            </h3>
            <div className="space-y-2" data-id="element-1584">
              <a href="#" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors" data-id="element-1585">
                <BookOpenIcon size={18} className="text-gray-400 mr-3" data-id="element-1586" />
                User Guide
              </a>
              <a href="#" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors" data-id="element-1587">
                <FileTextIcon size={18} className="text-gray-400 mr-3" data-id="element-1588" />
                API Documentation
              </a>
              <a href="#" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors" data-id="element-1589">
                <ClipboardIcon size={18} className="text-gray-400 mr-3" data-id="element-1590" />
                Release Notes
              </a>
              <a href="#" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors" data-id="element-1591">
                <BookOpenIcon size={18} className="text-gray-400 mr-3" data-id="element-1592" />
                Training Resources
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Support Request Form Modal */}
      {showSupportForm && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1593">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 relative" data-id="element-1594">
            <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-1595">
              <button onClick={() => setShowSupportForm(false)} className="text-gray-400 hover:text-gray-500" data-id="element-1596">
                <XIcon size={20} data-id="element-1597" />
              </button>
            </div>
            <div className="p-6" data-id="element-1598">
              <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1599">
                Submit Support Request
              </h2>
              {showSubmissionSuccess ? <div className="text-center py-8" data-id="element-1600">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4" data-id="element-1601">
                    <CheckCircleIcon size={24} className="text-green-600" data-id="element-1602" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1603">
                    Request Submitted Successfully
                  </h3>
                  <p className="text-gray-500" data-id="element-1604">
                    Your reference number is SR-12346
                  </p>
                </div> : <form onSubmit={handleSupportSubmit} data-id="element-1605">
                  <div className="space-y-4" data-id="element-1606">
                    <div data-id="element-1607">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700" data-id="element-1608">
                        Subject
                      </label>
                      <input type="text" id="subject" required maxLength={100} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1609" />
                    </div>
                    <div data-id="element-1610">
                      <label htmlFor="issue-type" className="block text-sm font-medium text-gray-700" data-id="element-1611">
                        Issue Type
                      </label>
                      <select id="issue-type" required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1612">
                        <option value="" data-id="element-1613">Select an issue type</option>
                        <option value="shipment" data-id="element-1614">Shipment Issue</option>
                        <option value="technical" data-id="element-1615">Technical Problem</option>
                        <option value="billing" data-id="element-1616">Billing Question</option>
                        <option value="feature" data-id="element-1617">Feature Request</option>
                      </select>
                    </div>
                    <div data-id="element-1618">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-1619">
                        Description
                      </label>
                      <textarea id="description" required rows={4} maxLength={1000} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1620"></textarea>
                    </div>
                    <div data-id="element-1621">
                      <label htmlFor="order-number" className="block text-sm font-medium text-gray-700" data-id="element-1622">
                        Order/Load Number (Optional)
                      </label>
                      <input type="text" id="order-number" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1623" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end" data-id="element-1624">
                    <button type="button" onClick={() => setShowSupportForm(false)} className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-1625">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-1626">
                      Submit Request
                    </button>
                  </div>
                </form>}
            </div>
          </div>
        </div>}
    </div>;
};
export default Support;